import React, { useEffect, useRef } from "react";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { useSiteContext } from "../../context/SiteContext";
import { useMeetingState } from "../../context/MeetingStateContext";

import { usePubSub } from "../../config/sdkConfig";

import useIsMobile from "../../hooks/useIsMobile";
import { X } from "lucide-react";

const TalkingPointSection = ({ sideBarContainerWidth, leftSidebarCollapsed = true, rightSidebarCollapsed = true }) => {
  const {
    activeElement,
    setActiveElement,
    meetingRole,
  } = useSiteContext();
  const { sideBarMode } = useMeetingAppContext();
  const meetingState = useMeetingState();
  const { publish: publishCloseElementSection } = usePubSub(
    "CLOSE_ELEMENT_SECTION"
  );
  const { publish: publishShowElement } = usePubSub("SHOW_ELEMENT_SECTION");
  const isMobile = useIsMobile();
  const autoHideTimerRef = useRef(null);

  // Parse the talking point settings (handle both string and object)
  const settings = typeof activeElement.settings === 'string'
    ? JSON.parse(activeElement.settings || '{}')
    : (activeElement.settings || {});
  const { text, style, color, background, placement, auto_hide_ms } = settings;

  // Calculate sidebar widths
  const leftSidebarWidth = leftSidebarCollapsed ? 56 : 380;
  const rightSidebarWidth = rightSidebarCollapsed ? 56 : 300;

  const handleClose = () => {
    const isHostRole = meetingRole === "moderator" || meetingRole === "co-host";
    if (isHostRole) {
      const elementId = activeElement?._id || activeElement?.id;
      publishCloseElementSection(JSON.stringify({ set: true }));
      publishShowElement(JSON.stringify({ clear: true }), { persist: true });
      if (elementId) meetingState.emitElementRemove(elementId);
    }
    setActiveElement(null);
  };

  // Auto-hide timer effect
  useEffect(() => {
    // Clear any existing timer
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }

    // Set up auto-hide timer if enabled
    if (auto_hide_ms && auto_hide_ms > 0) {
      autoHideTimerRef.current = setTimeout(() => {
        handleClose();
      }, auto_hide_ms);
    }

    // Cleanup on unmount or when activeElement changes
    return () => {
      if (autoHideTimerRef.current) {
        clearTimeout(autoHideTimerRef.current);
        autoHideTimerRef.current = null;
      }
    };
  }, [activeElement, auto_hide_ms]);

  // Get position styles based on placement
  const getPositionStyles = () => {
    // Layout structure:
    // - TopBar: h-16 (64px) at top
    // - Left sidebar: 56px collapsed, 380px expanded
    // - Right sidebar: 56px collapsed, 300px expanded (+ optional side panel)
    // - Bottom controls: h-14/h-16 (56-64px), fixed at bottom
    // - Main content area has p-2 md:p-3 padding (8-12px)

    const containerPadding = 12;
    const fromLeft = leftSidebarWidth + containerPadding;
    const fromRight = rightSidebarWidth + (sideBarMode ? sideBarContainerWidth : 0) + containerPadding;
    const topBarHeight = 64;
    const bottomBarHeight = 64;
    const fromTop = topBarHeight + containerPadding; // 76px
    const fromBottom = bottomBarHeight + containerPadding; // 76px

    if (isMobile) {
      return {
        position: 'fixed',
        left: '16px',
        right: '16px',
        bottom: '80px',
        zIndex: 25,
        display: 'flex',
        justifyContent: 'center',
      };
    }

    const baseStyle = {
      position: 'fixed',
      zIndex: 25,
    };

    switch (placement) {
      case 'top':
        return {
          ...baseStyle,
          top: `${fromTop}px`,
          left: `${fromLeft}px`,
          right: `${fromRight}px`,
          display: 'flex',
          justifyContent: 'center',
          maxHeight: `calc(100vh - ${fromTop + fromBottom}px)`,
        };
      case 'bottom':
        return {
          ...baseStyle,
          bottom: `${fromBottom}px`,
          left: `${fromLeft}px`,
          right: `${fromRight}px`,
          display: 'flex',
          justifyContent: 'center',
          maxHeight: `calc(100vh - ${fromTop + fromBottom}px)`,
        };
      case 'center':
        return {
          ...baseStyle,
          top: `${fromTop}px`,
          bottom: `${fromBottom}px`,
          left: `${fromLeft}px`,
          right: `${fromRight}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        };
      case 'right':
        return {
          ...baseStyle,
          top: `${fromTop}px`,
          right: `${fromRight}px`,
          maxWidth: '400px',
          maxHeight: `calc(100vh - ${fromTop + fromBottom}px)`,
        };
      case 'left':
      default:
        return {
          ...baseStyle,
          top: `${fromTop}px`,
          left: `${fromLeft}px`,
          maxWidth: '400px',
          maxHeight: `calc(100vh - ${fromTop + fromBottom}px)`,
        };
    }
  };

  // Render based on style
  const renderTalkingPoint = () => {
    const baseStyles = {
      backgroundColor: background || '#6a1fbf',
      color: color || '#ffffff',
    };

    switch (style) {
      case 'headline':
        return (
          <div
            className="px-6 py-4 rounded-xl text-center text-xl font-bold shadow-lg relative"
            style={baseStyles}
          >
            {text}
            <button
              onClick={handleClose}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        );
      case 'pill':
        return (
          <div
            className="px-4 py-2 rounded-full text-sm font-medium shadow-lg inline-flex items-center gap-2 relative"
            style={baseStyles}
          >
            {text}
            <button
              onClick={handleClose}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        );
      case 'ribbon':
        return (
          <div
            className="px-6 py-3 text-center font-medium shadow-lg relative"
            style={baseStyles}
          >
            {text}
            <button
              onClick={handleClose}
              className="absolute top-1/2 -translate-y-1/2 right-2 w-6 h-6 bg-red-500/80 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        );
      default: // lower-third
        return (
          <div
            className="px-5 py-3 rounded-lg shadow-lg inline-flex items-center gap-2 relative"
            style={baseStyles}
          >
            <span className="font-medium">{text}</span>
            <button
              onClick={handleClose}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        );
    }
  };

  // Stop event propagation to prevent closing on click
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      style={getPositionStyles()}
      className="transition-all duration-500 ease-in-out"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      onContextMenu={stopPropagation}
    >
      {renderTalkingPoint()}
    </div>
  );
};

export default TalkingPointSection;
